import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";

import HeroBgImg from "../assets/images/shooting.webp";

function PrestationCard({ title, description, price, note, onContact, onGallery, inView }) {
  const prefersReduced = useReducedMotion();

  const fadeUp = (delay = 0) =>
    prefersReduced
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: inView ? 1 : 0, y: inView ? 0 : 20 },
          transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1], delay },
        };

  return (
    <div className="presta-card">
      <motion.h2 className="presta-card__title" {...fadeUp(0)}>
        {title}
      </motion.h2>
      <div className="presta-card__divider" aria-hidden="true" />
      <motion.p className="presta-card__body" {...fadeUp(0.08)}>
        {description}
      </motion.p>
      <motion.p className="presta-card__price" {...fadeUp(0.14)}>
        {price}
      </motion.p>
      {note && (
        <motion.p className="presta-card__note" {...fadeUp(0.18)}>
          {note}
        </motion.p>
      )}
      <motion.div className="presta-card__actions" {...fadeUp(0.22)}>
        <button className="presta-btn presta-btn--primary" onClick={onContact} type="button">
          Me contacter
        </button>
        <button className="presta-btn presta-btn--secondary" onClick={onGallery} type="button">
          Voir la galerie
        </button>
      </motion.div>
    </div>
  );
}

function Tarif() {
  const navigate = useNavigate();

  const [refShooting, inViewShooting] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [refPortrait, inViewPortrait] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [refEvent,    inViewEvent]    = useInView({ triggerOnce: true, threshold: 0.15 });
  const [refMariage,  inViewMariage]  = useInView({ triggerOnce: true, threshold: 0.15 });

  const handleContactClick = () => navigate("/contact");
  const handleGalleryClick = (type) => navigate(`/gallery/${type}/undefined`);

  return (
    <main className="tarif-page">
      <SEO
        title="Prestations & Tarifs"
        path="/prestation"
        description="Tarifs et formules sur mesure : shooting photo, portrait, mariage, événement. Entre 80 € et 2500 €. Devis personnalisé à Lyon."
      />
      <Navigation />

      {/* ── HERO ─────────────────────────────────────── */}
      <section className="tarif-hero" aria-label="Prestations — introduction">
        <div className="tarif-hero__bg" aria-hidden="true">
          <img src={HeroBgImg} alt="" loading="eager" decoding="async" />
        </div>
        <div className="tarif-hero__overlay" aria-hidden="true" />
        <div className="tarif-hero__content">
          <p className="tarif-hero__eyebrow">Photographe&nbsp;·&nbsp;Lyon</p>
          <h1 className="tarif-hero__title">Prestations</h1>
          <p className="tarif-hero__tagline">Tarifs</p>
        </div>
      </section>

      {/* ── INTRO ────────────────────────────────────── */}
      <section className="tarif-intro" aria-label="Introduction">
        <div className="tarif-intro__inner">
          <div className="tarif-intro__left">
            <p className="tarif-intro__label">Chaque projet est unique</p>
            <h2 className="tarif-intro__heading">Des formules<br />sur mesure</h2>
            <p className="tarif-intro__body">
              Que ce soit pour un mariage, un concert, un portrait ou un shooting,
              chaque prestation est pensée pour correspondre exactement à votre vision.
              N'hésitez pas à me contacter pour un devis personnalisé.
            </p>
          </div>
          <div className="tarif-intro__right">
            <div className="tarif-intro__point">
              <span className="tarif-intro__point-num">01</span>
              <div>
                <p className="tarif-intro__point-title">Sur mesure</p>
                <p className="tarif-intro__point-desc">Chaque séance est pensée pour votre vision, votre style et votre budget — aucun forfait figé.</p>
              </div>
            </div>
            <div className="tarif-intro__point">
              <span className="tarif-intro__point-num">02</span>
              <div>
                <p className="tarif-intro__point-title">Retouche soignée</p>
                <p className="tarif-intro__point-desc">Toutes les photos livrées sont retouchées avec soin et remises en haute résolution.</p>
              </div>
            </div>
            <div className="tarif-intro__point">
              <span className="tarif-intro__point-num">03</span>
              <div>
                <p className="tarif-intro__point-title">Lyon & environs</p>
                <p className="tarif-intro__point-desc">Disponible sur Lyon, Villeurbanne et toute la région Auvergne-Rhône-Alpes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRESTATIONS ──────────────────────────────── */}
      <section className="tarif-list" aria-label="Liste des prestations">
        <div className="tarif-list__inner">

          <div ref={refShooting}>
            <PrestationCard
              title="Shooting Photo"
              description="Vous rêvez de photos naturelles et personnelles, prises en extérieur ou dans un lieu qui vous tient à cœur ? Je vous propose une séance sur-mesure, que ce soit pour un projet personnel, professionnel ou artistique. J'utilise la lumière naturelle et des décors variés pour un rendu authentique. Que ce soit pour un book, les réseaux ou simplement pour le plaisir, je vous guide avec bienveillance pour des poses naturelles et harmonieuses."
              price="Entre 100 € et 300 € pour une séance d'1h à 3h selon le lieu. Chaque heure supplémentaire : 75 € à 100 €."
              inView={inViewShooting}
              onContact={handleContactClick}
              onGallery={() => handleGalleryClick("shooting")}
            />
          </div>

          <div ref={refPortrait}>
            <PrestationCard
              title="Portrait"
              description="Un portrait, c'est bien plus qu'une photo : c'est un reflet de vous-même. Que ce soit pour un usage pro, artistique ou personnel, je vous accompagne pour créer une image fidèle à votre personnalité. Nous choisissons ensemble le lieu et l'ambiance qui vous ressemblent, et je mets un soin particulier à capter votre regard, vos expressions et votre lumière."
              price="Entre 80 € et 300 € pour 30 min à 2h de séance, avec 5 à 10 photos retouchées."
              inView={inViewPortrait}
              onContact={handleContactClick}
              onGallery={() => handleGalleryClick("portrait")}
            />
          </div>

          <div ref={refEvent}>
            <PrestationCard
              title="Événements"
              description="Concerts, anniversaires, baptêmes, soirées privées… Je capture l'ambiance et les temps forts de votre événement avec spontanéité et discrétion. Mon objectif : raconter votre moment en images, avec des photos vivantes, naturelles et remplies d'émotion."
              price="Entre 250 € et 2000 € pour une durée de 3 à 8h, clé USB incluse avec toutes les photos retouchées."
              inView={inViewEvent}
              onContact={handleContactClick}
              onGallery={() => handleGalleryClick("concert")}
            />
          </div>

          <div ref={refMariage}>
            <PrestationCard
              title="Mariage"
              description="Un mariage, c'est une histoire d'émotions, de regards, de rires et de souvenirs inoubliables. Je vous accompagne dès les préparatifs jusqu'à la soirée, avec une approche discrète et sensible pour capturer chaque instant. Mon objectif est de créer un reportage photo fidèle à votre couple, sincère et intemporel."
              price="Entre 1000 € et 2500 € pour une couverture complète (préparatifs, cérémonie, réception), avec 30 à 60 photos numériques et clé USB incluse."
              inView={inViewMariage}
              onContact={handleContactClick}
              onGallery={() => handleGalleryClick("mariage")}
            />
          </div>

        </div>
      </section>
    </main>
  );
}

export default Tarif;
